import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="mb-6 flex flex-wrap items-end justify-between gap-4 fade-up">
    <div>
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="page-title">{title}</h1>
      {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

const TONES = {
  default: "border-l-slate-900",
  blue: "border-l-blue-600",
  green: "border-l-emerald-600",
  amber: "border-l-amber-500",
  red: "border-l-red-600",
};

export const Kpi = ({ label, value, sub, tone = "default", testid, icon: Icon }) => (
  <div className={`rounded-md border border-slate-200 border-l-4 ${TONES[tone]} bg-white p-4 fade-up`} data-testid={testid}>
    <div className="flex items-center justify-between">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
      {Icon && <Icon className="h-4 w-4 text-slate-400" />}
    </div>
    <p className="mt-2 font-mono text-2xl font-bold tracking-tight text-slate-900 tabular" data-testid={testid && `${testid}-value`}>{value}</p>
    {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
  </div>
);

const BADGE = {
  OPEN: ["bg-amber-50 text-amber-700 border-amber-200", "Day Open"],
  REOPENED: ["bg-orange-50 text-orange-700 border-orange-200", "Reopened"],
  CLOSED: ["bg-emerald-50 text-emerald-700 border-emerald-200", "Submitted & Locked"],
  PENDING: ["bg-amber-50 text-amber-700 border-amber-200", "Pending"],
  APPROVED: ["bg-emerald-50 text-emerald-700 border-emerald-200", "Approved"],
  REJECTED: ["bg-red-50 text-red-700 border-red-200", "Rejected"],
  ACTIVE: ["bg-emerald-50 text-emerald-700 border-emerald-200", "Active"],
  INACTIVE: ["bg-slate-100 text-slate-600 border-slate-200", "Inactive"],
  VOID: ["bg-slate-100 text-slate-500 border-slate-200", "Void (reopened)"],
};

export const StatusBadge = ({ status, testid }) => {
  const [cls, label] = BADGE[status] || ["bg-slate-100 text-slate-600 border-slate-200", status];
  return (
    <span data-testid={testid} className={`inline-flex items-center whitespace-nowrap rounded border px-2 py-0.5 text-xs font-semibold ${cls}`}>
      {label}
    </span>
  );
};

export const Section = ({ title, actions, children, className = "", testid }) => (
  <section className={`rounded-md border border-slate-200 bg-white fade-up ${className}`} data-testid={testid}>
    {(title || actions) && (
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 px-4 py-3">
        <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700">{title}</h2>
        {actions}
      </div>
    )}
    <div className="p-4">{children}</div>
  </section>
);

export const Pager = ({ page, total = 0, limit, onPage, testid = "pager" }) => {
  const pages = Math.max(1, Math.ceil(total / limit));
  if (total <= limit) return null;
  return (
    <div className="flex items-center justify-end gap-2 pt-3 text-sm text-slate-600" data-testid={testid}>
      <span>Page {page} of {pages} · {total} records</span>
      <Button size="icon" variant="outline" className="h-8 w-8" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid={`${testid}-prev`}>
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <Button size="icon" variant="outline" className="h-8 w-8" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid={`${testid}-next`}>
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
};

export const Field = ({ label, children, hint }) => (
  <label className="block space-y-1.5">
    <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</span>
    {children}
    {hint && <span className="block text-xs text-slate-500">{hint}</span>}
  </label>
);

export const Loading = () => <div className="py-10 text-center text-sm text-slate-500" data-testid="loading">Loading…</div>;
